// Consent links — staff panel for the SECONDARY consent path (patient
// approves via magic link). Mirrors intake-links-panel.jsx; reuses its
// il-* styling wholesale. Mounted on the Onboard page (consent is
// enrollment work), gated by the consent_links_enabled practice setting.
import React, { useEffect, useState, useCallback, useRef } from 'react';
import QRCode from 'qrcode';
import { createConsentLink, listConsentLinks, resendConsentLink, voidConsentLink, absoluteUrl, consentSmsBody } from '../lib/consent-link-api';

const STATUS_LABEL = {
  sent: 'Sent', opened: 'Opened', approved: 'Approved ✓', expired: 'Expired', voided: 'Voided',
};
const STATUS_CLASS = {
  sent: 'il-badge-sent', opened: 'il-badge-opened', approved: 'il-badge-done',
  expired: 'il-badge-sent', voided: 'il-badge-sent',
};
const FLAG_LABEL = {
  already_signed: 'Consent was already on file — no change made',
  declined_on_file: 'Patient tapped agree but a decline is on file — call to reconcile',
  patient_missing: 'Patient record no longer exists — no change made',
};

const PROGRAM_LABEL = { ccm: 'CCM (99490)', bhi: 'BHI (99484)' };

// Fresh-link card: URL + copy/QR + the pre-composed SMS message. The raw
// token appears only here — it is never shown again after leaving the page.
export function ConsentFreshLink({ result, practiceName }) {
  const url = absoluteUrl(result.path);
  const sms = consentSmsBody(practiceName, url, result.expiresAt);
  const [copied, setCopied] = useState(null); // 'url' | 'sms'
  const [qr, setQr] = useState(null);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  useEffect(() => { QRCode.toDataURL(url, { width: 220 }).then(setQr).catch(() => {}); }, [url]);
  async function copy(kind, text) {
    try {
      await navigator.clipboard.writeText(text);
      if (!mountedRef.current) return;
      setCopied(kind);
      setTimeout(() => { if (mountedRef.current) setCopied(null); }, 1500);
    } catch (_) {}
  }
  return (
    <div className="il-fresh">
      <code>{url}</code>
      <div className="il-fresh-actions">
        <button onClick={() => copy('url', url)}>{copied === 'url' ? '✓ Copied' : 'Copy link'}</button>
        <button onClick={() => copy('sms', sms)}>{copied === 'sms' ? '✓ Copied' : 'Copy text message'}</button>
        <a href={`mailto:?subject=${encodeURIComponent('Your care program enrollment')}&body=${encodeURIComponent(sms)}`}>Email it</a>
      </div>
      {qr && <img className="il-qr" src={qr} alt="QR code for the consent link" />}
      {Array.isArray(result.programs) && result.programs.length > 0 && (
        <p className="il-note">Patient approval enrolls: <strong>{result.programs.map((x) => PROGRAM_LABEL[x] || x).join(' + ')}</strong></p>
      )}
      <p className="il-note">One-time use · expires {new Date(result.expiresAt).toLocaleDateString()} · save it now — it won't be shown after you leave this page</p>
    </div>
  );
}

// In-memory patient picker over the Onboard roster (the app-wide pattern —
// there is no shared typeahead component). Offered = active patients with at
// least one link-eligible program per `eligiblePrograms` (every unenrolled
// consent-pending patient rides for CCM; BHI needs an eligibility signal).
function PatientPicker({ patients, value, onChange, disabled, eligiblePrograms }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const eligible = (patients || []).filter((p) => p.active !== false && eligiblePrograms(p).length > 0);
  const q = query.trim().toLowerCase();
  const matches = q
    ? eligible.filter((p) => (p.name || '').toLowerCase().includes(q) || String(p.mrn || '').toLowerCase().includes(q)).slice(0, 8)
    : [];
  if (value) {
    return (
      <span className="il-note" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <strong>{value.name}</strong>{value.mrn ? ` (${value.mrn})` : ''}
        <button className="il-resend" disabled={disabled} onClick={() => { onChange(null); setQuery(''); }}>Change</button>
      </span>
    );
  }
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <input
        aria-label="Find patient by name or MRN"
        placeholder="Find unenrolled patient…"
        value={query}
        disabled={disabled}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && matches.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30,
          background: 'var(--panel, #fff)', border: '1px solid var(--line, #ccc)',
          borderRadius: 6, marginTop: 2, maxHeight: 240, overflowY: 'auto',
          boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
        }}>
          {matches.map((p) => (
            <button
              key={p.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange(p); setOpen(false); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px',
                background: 'none', border: 'none', cursor: 'pointer', fontSize: 13,
              }}>
              {p.name}{p.mrn ? ` · ${p.mrn}` : ''}
              <span style={{ float: 'right', color: 'var(--ink-2, #666)', fontSize: 12 }}>
                {eligiblePrograms(p).map((x) => (x === 'bhi' ? 'BHI' : 'CCM')).join(' + ')}
              </span>
            </button>
          ))}
        </div>
      )}
      {open && q && matches.length === 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30,
          background: 'var(--panel, #fff)', border: '1px solid var(--line, #ccc)',
          borderRadius: 6, marginTop: 2, padding: '7px 10px', fontSize: 13, color: 'var(--ink-2, #666)',
        }}>No link-eligible patient matches.</div>
      )}
    </span>
  );
}

// patients: adapted Onboard rows ({id, name, mrn, consent, active, raw}).
// reloadKey: bump to refresh the list after an inline (row-action) mint.
// linkProgramsFor: (patient) => ['ccm'|'bhi'] — which programs a link would
// enroll (Onboard owns the rule: settings + BHI eligibility signal). Fallback
// mirrors the pre-programs behavior: consent-pending → CCM.
export function ConsentLinksPanel({ patients, practiceName, reloadKey = 0, linkProgramsFor }) {
  const eligiblePrograms = linkProgramsFor || ((p) => (p.consent === 'pending' ? ['ccm'] : []));
  const [rows, setRows] = useState(null);
  const [picked, setPicked] = useState(null);
  const [sentTo, setSentTo] = useState('');
  const [fresh, setFresh] = useState(null); // { id, path, expiresAt }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const reload = useCallback(async () => {
    try {
      const data = await listConsentLinks();
      if (!mountedRef.current) return;
      setRows(data);
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e.message);
    }
  }, []);
  useEffect(() => { reload(); }, [reload, reloadKey]);

  async function onCreate() {
    if (!picked) return;
    setBusy(true); setError(null);
    try {
      const r = await createConsentLink(picked.id, sentTo.trim(), eligiblePrograms(picked));
      if (!mountedRef.current) return;
      setFresh(r); setPicked(null); setSentTo('');
      await reload();
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e.message);
    }
    finally { if (mountedRef.current) setBusy(false); }
  }

  async function onResend(id) {
    setBusy(true); setError(null);
    try {
      const r = await resendConsentLink(id);
      if (!mountedRef.current) return;
      setFresh(r);
      await reload();
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e.message);
    }
    finally { if (mountedRef.current) setBusy(false); }
  }

  async function onVoid(id) {
    setBusy(true); setError(null);
    try {
      await voidConsentLink(id);
      if (!mountedRef.current) return;
      await reload();
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e.message);
    }
    finally { if (mountedRef.current) setBusy(false); }
  }

  return (
    <div className="il-panel">
      <h3>Consent links</h3>
      <p className="il-note">
        Send a patient a secure page that explains the care-management program — they approve
        with one tap and are enrolled automatically (CCM starts at base 99490; the monthly tier
        stays your call). Documenting verbal consent in person still works exactly as before.
      </p>
      {error && <div className="il-error">Error: {error}</div>}
      <div className="il-form">
        <PatientPicker patients={patients} value={picked} onChange={setPicked} disabled={busy} eligiblePrograms={eligiblePrograms} />
        <input aria-label="Where you will send it" placeholder="Phone/email you'll send it to (optional)" value={sentTo} onChange={(e) => setSentTo(e.target.value)} />
        <button disabled={busy || !picked} onClick={onCreate}>Generate link</button>
      </div>
      {fresh && <ConsentFreshLink result={fresh} practiceName={practiceName} />}
      {rows && rows.length === 0 && <p className="il-note">No links yet.</p>}
      {rows && rows.length > 0 && (
        <table className="il-table">
          <thead><tr><th scope="col">Patient</th><th scope="col">Created</th><th scope="col">Status</th><th scope="col">Result</th><th scope="col" aria-label="Actions" /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.patientName || '—'}</td>
                <td>{new Date(r.createdAt).toLocaleDateString()}</td>
                <td><span className={`il-badge ${STATUS_CLASS[r.status] || ''}`}>{STATUS_LABEL[r.status] || r.status}</span></td>
                <td>
                  {r.status === 'approved' && r.approvedAt && !(r.flags || []).length && (
                    <span className="il-note">Consent signed {new Date(r.approvedAt).toLocaleDateString()}</span>
                  )}
                  {(r.flags || []).map((f) => <div key={f} className="il-flag">{FLAG_LABEL[f] || f}</div>)}
                </td>
                <td>
                  {['sent', 'opened', 'expired'].includes(r.status) && (
                    <>
                      <button className="il-resend" disabled={busy} aria-label={`Resend consent link for ${r.patientName}`} onClick={() => onResend(r.id)}>Resend</button>
                      {r.status !== 'expired' && (
                        <button className="il-resend" disabled={busy} aria-label={`Void consent link for ${r.patientName}`} onClick={() => onVoid(r.id)}>Void</button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
